import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AlertCircle, ArrowLeft, Plus, Loader2, Pencil } from 'lucide-react';
import { api } from '@/lib/api';
import type { GrAdminTierListResult } from '@/lib/api';
import { formatCreditsWithDollar } from '@/lib/format-credits';

type AdminTier = GrAdminTierListResult['tiers'][number];

/**
 * Tier presentation and duration are editable here. The Lit Pass credit price
 * is not: it lives on the tier's `arche_products` row and changes only by seed
 * migration, so the table shows it read-only.
 */
export function AdminTiersPage() {
  const [tiers, setTiers] = useState<AdminTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchTiers = useCallback(async () => {
    const id = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const result = await api.listGameRewardsAdminTiers();
      if (fetchIdRef.current !== id) return;
      setTiers(result.tiers);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load tiers');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchTiers();
    return () => {
      fetchIdRef.current++;
    };
  }, [fetchTiers]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<AdminTier | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Form state
  const [tierName, setTierName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [durationDays, setDurationDays] = useState('30');
  const [description, setDescription] = useState('');

  const resetForm = () => {
    setEditingTier(null);
    setTierName('');
    setDisplayName('');
    setDurationDays('30');
    setDescription('');
    setFormError(null);
  };

  const openEdit = (tier: AdminTier) => {
    setEditingTier(tier);
    setTierName(tier.tier);
    setDisplayName(tier.displayName);
    setDurationDays(String(tier.durationDays));
    setDescription(tier.description || '');
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    setSaving(true);
    setFormError(null);
    try {
      if (editingTier) {
        await api.updateGameRewardsTier(editingTier.tierId, {
          displayName: displayName.trim(),
          durationDays: Number(durationDays),
          description: description.trim() || undefined,
        });
      } else {
        if (!tierName.trim() || !displayName.trim()) return;
        await api.createGameRewardsTier({
          tier: tierName.trim(),
          displayName: displayName.trim(),
          durationDays: Number(durationDays),
          description: description.trim() || undefined,
        });
      }
      setDialogOpen(false);
      resetForm();
      fetchTiers();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to save tier');
    } finally {
      setSaving(false);
    }
  };

  const handleDialogChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_game_rewards/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Subscription Tiers</h1>
          <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={() => resetForm()}>
                <Plus className="h-4 w-4 mr-2" />
                Add Tier
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editingTier ? 'Edit Tier' : 'Create Subscription Tier'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSave} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="tierName">Tier Name *</Label>
                  <Input
                    id="tierName"
                    value={tierName}
                    onChange={(e) => setTierName(e.target.value)}
                    placeholder="bronze"
                    disabled={!!editingTier}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tierDisplayName">Display Name *</Label>
                  <Input
                    id="tierDisplayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Bronze Tier"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tierDuration">Duration (days)</Label>
                  <Input
                    id="tierDuration"
                    type="number"
                    min={1}
                    value={durationDays}
                    onChange={(e) => setDurationDays(e.target.value)}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  The credit price is seeded on the tier&apos;s store product and changes only by
                  migration.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="tierDescription">Description</Label>
                  <Textarea
                    id="tierDescription"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional description"
                    rows={3}
                  />
                </div>

                {formError && <p className="text-sm text-destructive">{formError}</p>}

                <Button type="submit" className="w-full" disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editingTier ? 'Update Tier' : 'Create Tier'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 rounded-lg" />
          ))}
        </div>
      ) : tiers.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">
          No subscription tiers configured. Click "Add Tier" to create one.
        </p>
      ) : (
        <Card>
          <CardContent className="pt-4">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tier</TableHead>
                  <TableHead>Display Name</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {tiers.map((tier) => (
                  <TableRow key={tier.tierId}>
                    <TableCell className="font-medium">{tier.tier}</TableCell>
                    <TableCell>{tier.displayName}</TableCell>
                    <TableCell>
                      {tier.priceCredits === null
                        ? '—'
                        : `${formatCreditsWithDollar(tier.priceCredits)} credits`}
                    </TableCell>
                    <TableCell>
                      {tier.durationDays} {tier.durationDays === 1 ? 'day' : 'days'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={tier.isActive ? 'default' : 'secondary'}>
                        {tier.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => openEdit(tier)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
